import { useEffect, useRef, useState } from "react";
import { ArrowDownToLine, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatusBadge, LoadingRows, TableShell, TR, TD } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function CreatorEarnings({ tab }) {
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [wdOpen, setWdOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [payForm, setPayForm] = useState({ upi_id: "", account_name: "", account_number: "", ifsc: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/vid/creator/me").then((r) => {
      setMe(r.data);
      const pd = r.data.payout_details || {};
      setPayForm({ upi_id: pd.upi_id || "", account_name: pd.account_name || "", account_number: pd.account_number || "", ifsc: pd.ifsc || "" });
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/vid/creator/earnings").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, []);
  const wdRef = useRef(null);
  useEffect(() => { if (tab === "withdrawals" && me && data) wdRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [tab, me, data]);

  if (!me || !data) return <LoadingRows rows={4} />;
  const e = me.earnings;
  const hasPayout = me.payout_details?.upi_id || me.payout_details?.account_number;

  const withdraw = async () => {
    setBusy(true);
    try { await api.post("/vid/creator/withdrawals", { amount: Number(amount) }); toast.success("Withdrawal requested — under review"); setWdOpen(false); setAmount(""); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const savePayout = async () => {
    setBusy(true);
    try { await api.post("/vid/creator/payout", payForm); toast.success("Payout details saved"); setPayOpen(false); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="creator-earnings-page">
      <PageHeader title="Earnings & Withdraw" subtitle="View Monetization + Affiliate + Referral earnings — one combined withdrawable balance" testid="earnings-title">
        <Button data-testid="creator-withdraw-btn" onClick={() => setWdOpen(true)} disabled={!hasPayout || (data.sources?.available ?? 0) < data.min_withdrawal}
          className="rounded-full font-bold gradient-bg text-white border-0 glow-sm">
          <ArrowDownToLine className="h-4 w-4 mr-1.5" />Withdraw
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Total Available", data.sources?.available ?? 0, "linear-gradient(135deg,#007A4D,#059669)", "Withdrawable now"],
          ["Monetization", (data.sources?.monetization?.earn_approved ?? 0) + (data.sources?.monetization?.earn_pending ?? 0), "linear-gradient(135deg,#2563EB,#06B6D4)", `${data.sources?.monetization?.eligible_views ?? 0} eligible views`],
          ["Affiliate", (data.sources?.affiliate?.approved ?? 0) + (data.sources?.affiliate?.pending ?? 0), "linear-gradient(135deg,#7C3AED,#DB2777)", "offer commissions"],
          ["Referrals", data.sources?.referral?.earned ?? 0, "linear-gradient(135deg,#d97706,#f59e0b)", "invite rewards"]].map(([l, v, bg, sub]) => (
          <div key={l} data-testid={`earn-${l.split(" ")[0].toLowerCase()}`} className="rounded-2xl p-5 text-white" style={{ background: bg }}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-white/75">{l}</p>
            <p className="font-display font-extrabold text-2xl mt-1.5">{fmtINR(v)}</p>
            <p className="text-[10px] text-white/70 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-5" data-testid="income-sources-card">
        <p className="font-display font-bold text-sm mb-3">Income Sources Breakdown</p>
        <div className="space-y-2.5">
          {[
            ["View Monetization", `${data.sources?.monetization?.eligible_views ?? 0} eligible views`, data.sources?.monetization?.earn_approved ?? 0, data.sources?.monetization?.earn_pending ?? 0, "mon-src"],
            ["Affiliate Commissions", `${data.conversions.length} conversions`, data.sources?.affiliate?.approved ?? 0, data.sources?.affiliate?.pending ?? 0, "aff-src"],
            ["Referral Rewards", "creator invite bonuses", data.sources?.referral?.earned ?? 0, 0, "ref-src"],
          ].map(([name, detail, approved, pending, tid]) => (
            <div key={tid} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-4 py-3" data-testid={tid}>
              <div>
                <p className="text-sm font-bold">{name}</p>
                <p className="text-[11px] text-muted-foreground">{detail}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400">{fmtINR(approved)} <span className="text-[10px] font-bold text-muted-foreground">available</span></p>
                {pending > 0 && <p className="text-[11px] font-bold text-amber-600">{fmtINR(pending)} pending review</p>}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">Paid so far: {fmtINR((e?.paid ?? 0) + (data.sources?.monetization?.earn_paid ?? 0))} · All available sources combine into your single withdrawal balance.</p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="creator-payout-card">
        <div>
          <p className="font-display font-bold text-sm flex items-center gap-2"><Wallet className="h-4 w-4" /> Payout Details</p>
          <p className="text-xs text-muted-foreground mt-0.5">{hasPayout ? `${me.payout_details.upi_id ? `UPI: ${me.payout_details.upi_id}` : ""} ${me.payout_details.account_number || ""}` : `Add UPI/bank details · Min withdrawal ${fmtINR(data.min_withdrawal)}`}</p>
        </div>
        <Button variant="outline" data-testid="creator-payout-edit" onClick={() => setPayOpen(true)} className="rounded-full font-bold">{hasPayout ? "Edit" : "Add Payout Details"}</Button>
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Affiliate Conversions</h2>
        {data.conversions.length === 0 ? <p className="text-sm text-muted-foreground">No conversions yet — share your video links!</p> : (
          <TableShell head={["Offer", "Sale Value", "Commission", "Status", "Date"]} testid="conversions-table">
            {data.conversions.map((c) => (
              <TR key={c.id} testid={`conv-${c.id}`}>
                <TD><span className="font-semibold text-sm">{c.offer_name}</span></TD>
                <TD>{fmtINR(c.sale_value)}</TD>
                <TD><span className="font-bold text-emerald-600 dark:text-emerald-400">{fmtINR(c.commission)}</span></TD>
                <TD><StatusBadge status={c.status} /></TD>
                <TD><span className="text-xs text-muted-foreground">{fmtDateTime(c.created_at)}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>

      <div ref={wdRef} id="withdrawals-section" data-testid="withdrawals-section">
        <h2 className="font-display font-bold text-lg mb-3">Withdrawals</h2>
        {data.withdrawals.length === 0 ? <p className="text-sm text-muted-foreground">No withdrawals yet.</p> : (
          <TableShell head={["Amount", "Status", "Reference", "Requested"]} testid="withdrawals-table">
            {data.withdrawals.map((w) => (
              <TR key={w.id} testid={`wd-${w.id}`}>
                <TD><span className="font-bold">{fmtINR(w.amount)}</span></TD>
                <TD><StatusBadge status={w.status} /></TD>
                <TD><span className="text-xs text-muted-foreground">{w.reference || "—"}</span></TD>
                <TD><span className="text-xs text-muted-foreground">{fmtDateTime(w.created_at)}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>

      <Dialog open={wdOpen} onOpenChange={setWdOpen}>
        <DialogContent data-testid="creator-withdraw-dialog">
          <DialogHeader><DialogTitle className="font-display">Request Withdrawal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Available balance (Monetization + Affiliate + Referrals): <b>{fmtINR(data.sources?.available ?? 0)}</b> · Minimum {fmtINR(data.min_withdrawal)}</p>
            <div className="space-y-1.5"><Label>Amount (₹)</Label><Input data-testid="creator-withdraw-amount" type="number" min={data.min_withdrawal} max={data.sources?.available ?? 0} value={amount} onChange={(ev) => setAmount(ev.target.value)} /></div>
            <Button data-testid="creator-withdraw-submit" onClick={withdraw} disabled={busy || !amount} className="w-full rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Withdraw ${amount ? fmtINR(amount) : ""}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="creator-payout-dialog">
          <DialogHeader><DialogTitle className="font-display">Payout Details</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>UPI ID</Label><Input data-testid="cpayout-upi" value={payForm.upi_id} onChange={(ev) => setPayForm({ ...payForm, upi_id: ev.target.value })} /></div>
            <div className="space-y-1.5"><Label>Account Holder Name</Label><Input data-testid="cpayout-name" value={payForm.account_name} onChange={(ev) => setPayForm({ ...payForm, account_name: ev.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Account Number</Label><Input data-testid="cpayout-acc" value={payForm.account_number} onChange={(ev) => setPayForm({ ...payForm, account_number: ev.target.value })} /></div>
              <div className="space-y-1.5"><Label>IFSC</Label><Input data-testid="cpayout-ifsc" value={payForm.ifsc} onChange={(ev) => setPayForm({ ...payForm, ifsc: ev.target.value })} /></div>
            </div>
            <Button data-testid="cpayout-save" onClick={savePayout} disabled={busy} className="w-full rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
